import { useEffect, useRef, useCallback } from "react";
import { useKioskActivity } from "@/hooks/useKioskActivity";
import { KioskActivityPrompt } from "@/components/KioskActivityPrompt";
import { usePlatform } from "@/hooks/usePlatform";
import { PdfViewer } from "@/components/PdfViewer";
import { KioskBottomBar } from "@/components/KioskBottomBar";

interface WebViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  blockedDomains?: string[];
  allowedDomains?: string[];
}

const INVALID_SCHEMES = ['mailto:', 'tel:', 'javascript:', 'data:', 'blob:'];

const getDomain = (url: string) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return '';
  }
};

// Detectar si la URL es un PDF
const isPdfUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
};

// URL de la API (pdf-proxy migrado de Supabase Edge Functions)
const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

// Detectar si es un PDF alojado en storage local (MinIO)
const isLocalStoragePdf = (url: string): boolean => {
  return isPdfUrl(url) && !url.startsWith('https://docs.google.com');
};

// Transformar URL de PDF para usar el proxy o Google Docs Viewer
const getEmbeddableUrl = (
  url: string,
  blockedDomains: string[] = [],
  allowedDomains: string[] = [],
): string => {
  if (isPdfUrl(url)) {
    // Si es un PDF del storage local, usar el proxy para evitar bloqueos de Cyma
    if (isLocalStoragePdf(url)) {
      const proxyUrl = `${API_URL}/pdf-proxy?url=${encodeURIComponent(url)}`;
      console.log('📄 Cargando PDF local via proxy:', proxyUrl);
      return proxyUrl;
    }
    // PDFs externos: usar Google Docs Viewer como fallback
    console.log('📄 Cargando PDF externo via Google Docs Viewer');
    return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
  }
  // Páginas web: usar proxy para eliminar X-Frame-Options / CSP frame-ancestors
  const blockedQuery = blockedDomains.length > 0
    ? `&blocked=${encodeURIComponent(blockedDomains.join(','))}`
    : '';
  const allowedQuery = allowedDomains.length > 0
    ? `&allowed=${encodeURIComponent(allowedDomains.join(','))}`
    : '';
  const proxyUrl = `${API_URL}/web-proxy?url=${encodeURIComponent(url)}${blockedQuery}${allowedQuery}`;
  console.log('🌐 Cargando web via proxy:', proxyUrl);
  return proxyUrl;
};

// Obtener el dominio efectivo (considerando Google Docs para PDFs y el proxy para webs)
const getEffectiveDomain = (originalUrl: string): string => {
  if (isPdfUrl(originalUrl)) {
    return 'docs.google.com';
  }
  // Las webs van por el proxy de la API: el dominio efectivo es el de la API
  if (API_URL) {
    try { return new URL(API_URL).hostname; } catch { /* fallback */ }
  }
  return getDomain(originalUrl);
};

export const WebViewModal = ({
  isOpen,
  onClose,
  url,
  title,
  blockedDomains = [],
  allowedDomains = [],
}: WebViewModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const initialDomain = useRef(getEffectiveDomain(url));
  const proxyReadyAt = useRef(0);
  const { isAndroid } = usePlatform();

  // Manejador de cierre robusto
  const handleClose = useCallback(() => {
    console.log('🏠 Botón INICIO presionado');
    onClose();
  }, [onClose]);

  const handleNoResponse = useCallback(() => {
    console.log('⏰ Sin respuesta del usuario en WebView - activando salvapantallas');
    onClose();
    window.dispatchEvent(new Event('forceScreensaver'));
  }, [onClose]);

  const { showPrompt, confirmActivity, remainingSeconds } = useKioskActivity({
    promptInterval: 120000,
    responseTimeout: 15000,
    onNoResponse: handleNoResponse,
    enabled: isOpen
  });

  useEffect(() => {
    initialDomain.current = getEffectiveDomain(url);
  }, [url]);

  // El documento inyectado por el proxy confirma que cada navegación sigue
  // dentro del entorno controlado y comunica la actividad real del usuario.
  useEffect(() => {
    if (!isOpen) return;
    proxyReadyAt.current = 0;

    const handleProxyMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'kiosk:webview-activity') {
        window.dispatchEvent(new Event('webViewActivity'));
      } else if (event.data?.type === 'kiosk:proxy-ready') {
        proxyReadyAt.current = Date.now();
      }
    };

    window.addEventListener('message', handleProxyMessage);
    return () => window.removeEventListener('message', handleProxyMessage);
  }, [isOpen]);

  // Interceptor de clics en iframe (solo funciona para same-origin)
  useEffect(() => {
    if (!isOpen || !iframeRef.current) return;

    const iframe = iframeRef.current;
    const verificationTimers = new Set<ReturnType<typeof setTimeout>>();

    const handleIframeLoad = () => {
      if (!isPdfUrl(url)) {
        const timer = setTimeout(() => {
          verificationTimers.delete(timer);
          if (Date.now() - proxyReadyAt.current > 1500) {
            console.warn('Navegación fuera del proxy detectada, restaurando URL');
            iframe.src = getEmbeddableUrl(url, blockedDomains, allowedDomains);
          }
        }, 500);
        verificationTimers.add(timer);
      }

      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) return;

        iframeDoc.addEventListener('click', (e: MouseEvent) => {
          const target = e.target as HTMLElement;
          const anchor = target.closest('a');

          if (anchor) {
            const href = anchor.getAttribute('href') || '';
            if (INVALID_SCHEMES.some(scheme => href.toLowerCase().startsWith(scheme))) {
              e.preventDefault();
              e.stopPropagation();
              console.log('⛔ Clic en enlace bloqueado:', href);
              return false;
            }
          }
        }, true);

        console.log('✅ Interceptor de clics inyectado en iframe');
      } catch (e) {
        console.warn('⚠️ No se pudo inyectar interceptor (CORS)');
      }
    };

    iframe.addEventListener('load', handleIframeLoad);

    return () => {
      iframe.removeEventListener('load', handleIframeLoad);
      verificationTimers.forEach(clearTimeout);
    };
  }, [isOpen, url, blockedDomains, allowedDomains]);

  // Detección de navegación externa
  useEffect(() => {
    if (!isOpen) return;

    const checkInterval = setInterval(() => {
      try {
        const iframe = iframeRef.current;
        if (!iframe || !iframe.isConnected || !iframe.contentWindow) return;

        try {
          const currentHref = iframe.contentWindow.location.href;

          if (INVALID_SCHEMES.some(scheme => currentHref.startsWith(scheme))) {
            console.log('⛔ Esquema no válido detectado, restaurando URL:', currentHref);
            iframe.src = url;
            return;
          }

          const currentDomain = getDomain(currentHref);

          if (currentDomain && currentDomain !== initialDomain.current) {
            console.warn('Navegación externa detectada, cerrando modal');
            clearInterval(checkInterval);
            onClose();
          }
        } catch (corsError) {
          // Error CORS esperado
        }
      } catch (error) {
        console.error('Error crítico en WebViewModal:', error);
        clearInterval(checkInterval);
        onClose();
      }
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [isOpen, onClose, url]);

  if (!isOpen) return null;

  // Si es un PDF local, usar el visor PDF.js
  if (isLocalStoragePdf(url)) {
    return <PdfViewer url={url} onClose={onClose} />;
  }

  return (
    <>
      {/* 
        ESTRUCTURA: El contenedor usa flexbox vertical
        - El iframe ocupa todo el espacio disponible (flex: 1)
        - El área del botón está DEBAJO del iframe, en el flujo normal del DOM
        - El iframe NO cubre el área del botón
      */}
      <div className="webview-container">
        {/* Iframe con el contenido externo - ocupa todo MENOS el área del botón */}
        <iframe
          ref={iframeRef}
          src={getEmbeddableUrl(url, blockedDomains, allowedDomains)}
          className="webview-iframe-fullscreen"
          title={title || "Contenido externo"}
          allow="geolocation; microphone; camera"
          sandbox="allow-same-origin allow-scripts allow-forms"
          loading="eager"
          onError={() => {
            console.log('❌ Error en iframe, restaurando URL');
            if (iframeRef.current) {
              iframeRef.current.src = getEmbeddableUrl(url, blockedDomains, allowedDomains);
            }
          }}
        />

        {/* 
          ÁREA DEL BOTÓN - está DEBAJO del iframe, no superpuesta
          El iframe no cubre esta área, así que los eventos táctiles
          llegan directamente al botón sin pasar por el iframe
        */}
        <KioskBottomBar onBack={handleClose} onHome={handleClose} />
      </div>

      {showPrompt && (
        <KioskActivityPrompt
          isVisible={showPrompt}
          onConfirm={confirmActivity}
          remainingSeconds={remainingSeconds}
        />
      )}
    </>
  );
};

